import {
  CheckCircle2,
  CircleDot,
  CornerUpLeft,
  KeyRound,
  PauseCircle,
  PencilLine,
  PlayCircle,
  Send,
  Undo2,
  Upload,
  Wallet,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import {
  useStorefrontInvites,
  type AdminStorefrontDetail,
  type AdminStorefrontEvent,
  type StorefrontImageStyle,
  type StorefrontTemplate,
} from '@/lib/api/storefrontAdminHooks';

import {
  IMAGE_STYLE_LABEL,
  TEMPLATE_LABEL,
  count,
  dateTime,
  planLabel,
  productArtUrls,
  safeHex,
  safeHttpUrl,
  shortDate,
  templateImageUrl,
  usd,
} from './storefrontFormat';
import {
  EVENT_LABEL,
  eventKey,
  eventSentence,
  readableNote,
  readablePolicyNote,
} from './storeChanges';
import { ChangeList } from './storeDiff';
import { couponChecks, type CouponCheck } from './storeCompliance';
import { FallbackImage, Field, Notice, Panel, Pill, Stat, StoreMark, Td, Th } from './ui';

const SECTION_LABEL: Record<string, string> = {
  hero: 'Hero',
  trust: 'Trust strip',
  how_it_works: 'How it works',
  products: 'Products',
  featured_product: 'Featured product',
  about: 'About',
  lifestyle: 'Lifestyle',
  faq: 'FAQ',
};

function Swatch({ label, hex }: { label: string; hex: string | null | undefined }) {
  if (!hex) {
    return (
      <div className="flex items-center gap-8">
        <span className="w-24 h-24 border border-dashed border-line" aria-hidden />
        <span className="flex flex-col gap-2">
          <span className="type-label-xs text-ink-muted">{label}</span>
          <span className="type-body-sm text-ink-muted">None</span>
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-8">
      <span
        className="w-24 h-24 border border-line-subtle"
        style={{ background: safeHex(hex) }}
        aria-hidden
      />
      <span className="flex flex-col gap-2">
        <span className="type-label-xs text-ink-muted">{label}</span>
        <span className="font-mono text-12 text-ink uppercase">{hex}</span>
      </span>
    </div>
  );
}

export function BrandPanel({ store }: { store: AdminStorefrontDetail }) {
  const design = store.design ?? {};
  const template = (design.template ?? 'clinical') as StorefrontTemplate;
  const style = (design.product_image_style ?? 'clinical-white') as StorefrontImageStyle;
  const primary = design.theme?.primary ?? store.accent_color;
  const hero = design.sections?.find((s) => s.key === 'hero');
  // What patients see: the owner's own hero photo first, else the
  // template image the owner picked, else no image (a text-only hero).
  const heroPhoto = safeHttpUrl(
    typeof hero?.content?.photo_url === 'string' ? (hero.content.photo_url as string) : null,
  );
  const heroImageId = hero?.content && 'image_id' in hero.content ? hero.content.image_id : 'hero-1';
  const heroUrl =
    heroPhoto ?? (typeof heroImageId === 'string' ? templateImageUrl(template, heroImageId) : null);
  const heroSource = heroPhoto ? 'Owner photo' : heroUrl ? 'Template image' : 'No image (text-only hero)';
  const sections = design.sections ?? [];
  const sampleProduct = store.products[0]?.canonical_product_id;

  return (
    <Panel title="Brand" bodyClassName="p-0">
      <div className="relative aspect-[16/7] bg-sunken border-b border-line-subtle overflow-hidden">
        <FallbackImage
          candidates={heroUrl ? [heroUrl] : []}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {!heroUrl && (
          <div className="absolute inset-0" style={{ background: safeHex(primary) }} aria-hidden />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
        <span className="absolute top-10 left-10">
          <Pill tone={heroPhoto ? 'info' : 'neutral'}>{heroSource}</Pill>
        </span>
        <div className="absolute left-14 right-14 bottom-12 flex items-end gap-10">
          <StoreMark name={store.name} logoUrl={store.logo_url} accent={primary} size={44} />
          <div className="flex flex-col gap-4 min-w-0 text-white">
            <span className="type-label-xs text-white/80">
              {TEMPLATE_LABEL[template] ?? template} template
            </span>
            <span className="type-body-md truncate">{store.headline || store.name}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-16 p-16">
        <div className="flex flex-wrap gap-16">
          <Swatch label="Primary" hex={primary} />
          <Swatch label="Secondary" hex={design.theme?.secondary ?? null} />
        </div>
        <dl className="grid grid-cols-2 gap-14">
          <Field label="Template">{TEMPLATE_LABEL[template] ?? template}</Field>
          <Field label="Product images">
            <span className="flex items-center gap-8">
              {sampleProduct && (
                <span className="w-24 h-24 bg-sunken border border-line-subtle overflow-hidden shrink-0">
                  <FallbackImage
                    candidates={productArtUrls(sampleProduct, style)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </span>
              )}
              {IMAGE_STYLE_LABEL[style] ?? style}
            </span>
          </Field>
          {store.subheadline && (
            <Field label="Subheadline" className="col-span-2">
              {store.subheadline}
            </Field>
          )}
        </dl>
        {sections.length > 0 && (
          <div className="flex flex-col gap-8">
            <span className="type-label-xs text-ink-muted">Page sections, in order</span>
            <ol className="flex flex-wrap gap-6">
              {sections.map((section, i) => (
                <li
                  key={section.key}
                  className={cn(
                    'inline-flex items-center gap-6 h-24 px-8 border type-label-xs',
                    section.enabled
                      ? 'border-line text-ink'
                      : 'border-dashed border-line text-ink-muted line-through',
                  )}
                  title={section.enabled ? undefined : 'Hidden'}
                >
                  <span className="text-ink-muted tabular-nums">{i + 1}</span>
                  {SECTION_LABEL[section.key] ?? section.key}
                </li>
              ))}
              <li className="inline-flex items-center gap-6 h-24 px-8 border border-line-subtle bg-sunken type-label-xs text-ink-muted">
                Safety footer
              </li>
            </ol>
          </div>
        )}
      </div>
    </Panel>
  );
}

export function TeamPanel({ store }: { store: AdminStorefrontDetail }) {
  const members = [...store.members].sort(
    (a, b) => Number(b.access === 'owner') - Number(a.access === 'owner'),
  );
  const owner = members.find((m) => m.access === 'owner');
  const noOwner = !owner && !store.owner_email;
  // The invite that created the store: who it was for (its note) and its
  // code's last group. The list endpoint carries both.
  const invites = useStorefrontInvites();
  const invite = invites.data?.find((i) => i.storefront_id === store.id) ?? null;
  const codeTail = invite?.code.split('-').pop();
  return (
    <Panel title="Owner and team" aside={<span className="type-label-xs text-ink-muted">{members.length}</span>}>
      <div className="flex flex-col gap-14">
        {noOwner && (
          <Notice tone={store.status === 'live' ? 'danger' : 'warning'}>
            No owner. Nobody can edit this store{store.status === 'live' ? ', and its payouts have nobody to go to' : ''}.
          </Notice>
        )}
        <dl className="grid grid-cols-2 gap-14">
          <Field label="Owner" className="col-span-2">
            {owner?.full_name ? (
              <span className="flex flex-col gap-2">
                <span>{owner.full_name}</span>
                <span className="text-ink-secondary">{owner.email ?? store.owner_email ?? '—'}</span>
              </span>
            ) : (
              store.owner_email || owner?.email || '—'
            )}
          </Field>
          <Field label="Invite" className="col-span-2">
            {invite ? (
              <span className="flex flex-col gap-2">
                <span className="font-mono text-12">••••-••••-{codeTail}</span>
                <span className="text-ink-secondary">
                  {invite.note ? `“${invite.note}”` : 'No note'}
                  {invite.redeemed_at ? ` · redeemed ${shortDate(invite.redeemed_at)}` : ''}
                </span>
              </span>
            ) : invites.isLoading ? (
              '…'
            ) : (
              <span className="text-ink-muted">Created without an invite</span>
            )}
          </Field>
          <Field label="Created">{shortDate(store.created_at)}</Field>
          <Field label="Last edit">{dateTime(store.updated_at)}</Field>
        </dl>
        {members.length === 0 ? (
          <p className="type-body-sm text-ink-muted">No members yet.</p>
        ) : (
          <ul className="flex flex-col border border-line-subtle">
            {members.map((member, i) => (
              <li
                key={`${member.email ?? 'member'}-${i}`}
                className="flex items-center justify-between gap-10 px-12 py-10 border-b border-line-subtle last:border-b-0"
              >
                <span className="flex flex-col gap-2 min-w-0">
                  <span className="type-body-sm text-ink truncate">{member.email || '—'}</span>
                  <span className="type-label-xs text-ink-muted">
                    Joined {shortDate(member.joined_at)}
                  </span>
                </span>
                <Pill tone={member.access === 'owner' ? 'accent' : 'neutral'}>
                  {member.access === 'owner' ? 'Owner' : 'Editor'}
                </Pill>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

export function ProductsPanel({ store }: { store: AdminStorefrontDetail }) {
  const style = store.design?.product_image_style ?? 'clinical-white';
  const planCount = store.products.reduce((n, p) => n + p.plans.length, 0);
  return (
    <Panel
      title="Products and plans"
      aside={
        <span className="type-label-xs text-ink-muted">
          {store.products.length} product{store.products.length === 1 ? '' : 's'} · {planCount}{' '}
          plan{planCount === 1 ? '' : 's'}
        </span>
      }
      bodyClassName="p-0"
    >
      {store.products.length === 0 ? (
        <p className="px-16 py-20 type-body-sm text-ink-muted">No products yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[68rem]">
            <thead className="bg-raised border-b border-line-subtle">
              <tr>
                <Th>Product</Th>
                <Th>Plan</Th>
                <Th align="right">Price / term</Th>
                <Th align="right">Floor</Th>
                <Th align="right">Store earns</Th>
                <Th>State</Th>
              </tr>
            </thead>
            <tbody>
              {store.products.map((product) =>
                (product.plans.length ? product.plans : [null]).map((plan, i, all) => {
                  const belowFloor =
                    plan != null && plan.floor_cents != null && plan.price_cents < plan.floor_cents;
                  const earning =
                    plan?.earning_cents ??
                    (plan && plan.floor_cents != null ? plan.price_cents - plan.floor_cents : null);
                  return (
                    <tr
                      key={`${product.canonical_product_id}-${plan?.plan ?? 'none'}`}
                      className={cn(
                        i === all.length - 1 && 'border-b border-line-subtle last:border-b-0',
                        plan && !plan.active && 'text-ink-muted',
                      )}
                    >
                      {i === 0 && (
                        <Td className="align-top" colSpan={1}>
                          <div className="flex items-center gap-10 min-w-0">
                            <span className="w-40 h-40 shrink-0 bg-sunken border border-line-subtle overflow-hidden">
                              <FallbackImage
                                candidates={productArtUrls(product.canonical_product_id, style)}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </span>
                            <span
                              className="flex flex-col gap-3 min-w-0 max-w-[22rem]"
                              title={product.canonical_product_id}
                            >
                              <span className="type-body-sm text-ink truncate">
                                {product.name || product.canonical_product_id}
                              </span>
                              <span className="font-mono text-10 text-ink-muted truncate">
                                {product.form ? `${product.form} · ` : ''}
                                {product.canonical_product_id}
                              </span>
                            </span>
                          </div>
                        </Td>
                      )}
                      {i !== 0 && <Td />}
                      {plan ? (
                        <>
                          <Td className="whitespace-nowrap">{planLabel(plan.plan, plan.interval_months)}</Td>
                          <Td align="right" className={belowFloor ? 'text-warning' : 'text-ink'}>
                            {usd(plan.price_cents)}
                          </Td>
                          <Td align="right" className="text-ink-secondary">
                            {plan.floor_cents == null ? 'n/a' : usd(plan.floor_cents)}
                          </Td>
                          <Td
                            align="right"
                            className={
                              earning != null && earning > 0
                                ? 'text-success'
                                : plan.active && earning != null
                                  ? 'text-warning'
                                  : 'text-ink-secondary'
                            }
                          >
                            {earning == null ? '—' : usd(earning)}
                          </Td>
                          <Td>
                            <span className="flex flex-wrap items-center gap-6">
                              {belowFloor ? (
                                <Pill tone="warning">Below floor</Pill>
                              ) : plan.active ? (
                                <Pill tone="success">Active</Pill>
                              ) : (
                                <Pill>Off</Pill>
                              )}
                              {plan.active && !belowFloor && earning != null && plan.floor_cents != null && (
                                earning <= 0 ? (
                                  <Pill tone="warning" className="normal-case">
                                    Earns $0
                                  </Pill>
                                ) : earning < plan.floor_cents * 0.1 ? (
                                  <Pill tone="warning" className="normal-case">
                                    Thin margin
                                  </Pill>
                                ) : null
                              )}
                            </span>
                          </Td>
                        </>
                      ) : (
                        <Td colSpan={5} className="text-ink-muted">
                          No plans
                        </Td>
                      )}
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-16 py-10 border-t border-line-subtle type-body-sm text-ink-muted">
        Floor is our price for the term; the store earns the price above it. Owners cannot price
        below floor. A plan priced at floor earns the store $0, so no coupon takes anything off
        it.
      </p>
    </Panel>
  );
}

function promoDiscount(p: AdminStorefrontDetail['promo_codes'][number]): string {
  if (p.description) return p.description;
  const amount = p.percent_off != null ? `${p.percent_off}% off` : usd(p.amount_off_cents) + ' off';
  return p.duration === 'forever' ? `${amount} every charge` : `${amount} the first charge`;
}

function sharePct(share: number): string {
  const pct = share * 100;
  return `${pct > 0 && pct < 1 ? pct.toFixed(1) : Math.round(pct)}%`;
}

/** What a coupon actually takes off the active plans (the floor caps a
 *  store-funded discount at the store's earning). */
function CouponReachCell({ check }: { check: CouponCheck | undefined }) {
  if (!check || check.reach.length === 0) return <span className="text-ink-muted">—</span>;
  if (check.cappedPlans === 0) {
    return <span className="text-ink-secondary">As described on every plan</span>;
  }
  const range =
    check.minShare === check.maxShare
      ? sharePct(check.maxShare)
      : `${sharePct(check.minShare)}–${sharePct(check.maxShare)}`;
  // The plan the floor cuts hardest (least of the advertised discount).
  const worst = check.reach
    .filter((r) => r.deliveredCents < r.advertisedCents)
    .sort(
      (a, b) =>
        a.deliveredCents / Math.max(a.advertisedCents, 1) -
        b.deliveredCents / Math.max(b.advertisedCents, 1),
    )[0];
  return (
    <span className="flex flex-col gap-4">
      <span className="flex flex-wrap items-center gap-6">
        <Pill tone="warning">{check.ineffective ? 'No discount on any plan' : `Capped on ${check.cappedPlans} of ${check.reach.length}`}</Pill>
        {!check.ineffective && <span className="text-ink">{range} off</span>}
      </span>
      {worst && (
        <span className="type-body-sm text-ink-muted">
          {worst.productName} · {worst.planLabel}: {usd(worst.deliveredCents)} off{' '}
          {usd(worst.priceCents)} (advertised {usd(worst.advertisedCents)})
        </span>
      )}
    </span>
  );
}

export function CouponsPanel({ store }: { store: AdminStorefrontDetail }) {
  const codes = store.promo_codes;
  const checks = new Map(couponChecks(store).map((c) => [c.code, c]));
  const short = [...checks.values()].filter((c) => c.cappedPlans > 0);
  return (
    <Panel
      title="Coupons"
      aside={<span className="type-label-xs text-ink-muted">{codes.length}</span>}
      bodyClassName="p-0"
    >
      {short.length > 0 && (
        <p className="px-16 py-10 border-b border-line-subtle bg-warning-wash type-body-sm text-ink leading-130">
          {short.map((c) => c.code).join(', ')}{' '}
          {short.length === 1 ? 'promises' : 'promise'} more than the store can fund on some plans:
          the discount cannot take a price below floor, so patients get less than the description
          says. Ask the owner to lower the discount or raise those prices.
        </p>
      )}
      {codes.length === 0 ? (
        <p className="px-16 py-20 type-body-sm text-ink-muted">No coupons.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[72rem]">
            <thead className="bg-raised border-b border-line-subtle">
              <tr>
                <Th>Code</Th>
                <Th>Discount</Th>
                <Th>Patients get</Th>
                <Th align="right">Used</Th>
                <Th>Expires</Th>
                <Th>State</Th>
              </tr>
            </thead>
            <tbody>
              {codes.map((p) => (
                <tr key={p.id ?? p.code} className="border-b border-line-subtle last:border-b-0">
                  <Td className="type-data-sm">{p.code}</Td>
                  <Td className="text-ink-secondary">{promoDiscount(p)}</Td>
                  <Td>
                    {p.active ? (
                      <CouponReachCell check={checks.get(p.code)} />
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                  </Td>
                  <Td align="right">
                    {count(p.redemptions ?? 0)}
                    {p.max_redemptions != null && (
                      <span className="text-ink-muted"> / {count(p.max_redemptions)}</span>
                    )}
                  </Td>
                  <Td className="text-ink-secondary whitespace-nowrap">
                    {p.expires_at ? shortDate(p.expires_at) : 'Never'}
                  </Td>
                  <Td>{p.active ? <Pill tone="success">Active</Pill> : <Pill>Off</Pill>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-16 py-10 border-t border-line-subtle type-body-sm text-ink-muted">
        Store-funded: a discount only comes out of the store&apos;s earnings and never takes a
        price below floor.
      </p>
    </Panel>
  );
}

function percent(part: number, whole: number): string {
  if (!whole || !Number.isFinite(part) || !Number.isFinite(whole)) return '—';
  const value = (part / whole) * 100;
  return `${value < 10 ? value.toFixed(1) : Math.round(value)}%`;
}

export function MetricsPanel({ store }: { store: AdminStorefrontDetail }) {
  const m = store.metrics;
  // A store with no orders yet: one line instead of ten zero tiles.
  const empty = !m || (m.orders === 0 && m.paid_charges === 0 && m.gross_cents === 0);
  return (
    <Panel title="Performance" aside={<span className="type-label-xs text-ink-muted">All time</span>}>
      {empty ? (
        <p className="type-body-sm text-ink-muted">
          No orders yet{m ? ` · ${count(m.visits)} store visit${m.visits === 1 ? '' : 's'}` : ''}.
          Aggregates only; no patient details.
        </p>
      ) : !m ? null : (
        <div className="flex flex-col gap-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            <Stat label="Gross sales" value={usd(m.gross_cents)} emphasis />
            <Stat label="Store earnings" value={usd(m.earnings_cents)} />
            <Stat label="Refunds" value={usd(m.refunds_cents ? -Math.abs(m.refunds_cents) : 0)} />
            <Stat label="Visits" value={count(m.visits)} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            <Stat label="Orders" value={count(m.orders)} />
            <Stat label="Paid charges" value={count(m.paid_charges)} />
            <Stat label="Active plans" value={count(m.active_subscriptions)} />
            <Stat label="Customers" value={count(m.customers)} />
          </div>
          <div className="grid grid-cols-2 gap-10">
            <Stat
              label="Visit to purchase"
              value={percent(m.paid_charges, m.visits)}
              hint="Paid checkouts per store visit"
            />
            <Stat
              label="Refund rate"
              value={percent(Math.abs(m.refunds_cents), m.gross_cents)}
              hint="Refunded or disputed share of sales"
            />
          </div>
          <p className="type-body-sm text-ink-muted">
            All time. Aggregates only; no patient details.
          </p>
        </div>
      )}
    </Panel>
  );
}

const EVENT_META: Record<string, { icon: typeof Send; tone: string }> = {
  submitted: { icon: Send, tone: 'text-info' },
  resubmitted: { icon: Send, tone: 'text-info' },
  approved: { icon: CheckCircle2, tone: 'text-success' },
  changes_requested: { icon: CornerUpLeft, tone: 'text-warning' },
  rejected: { icon: XCircle, tone: 'text-danger' },
  paused: { icon: PauseCircle, tone: 'text-warning' },
  unpaused: { icon: PlayCircle, tone: 'text-success' },
  invite_redeemed: { icon: KeyRound, tone: 'text-ink-secondary' },
  edited: { icon: PencilLine, tone: 'text-ink-secondary' },
  changes_published: { icon: Upload, tone: 'text-info' },
  changes_submitted: { icon: Send, tone: 'text-info' },
  changes_approved: { icon: CheckCircle2, tone: 'text-success' },
  changes_rejected: { icon: XCircle, tone: 'text-danger' },
  reverted: { icon: Undo2, tone: 'text-warning' },
  marked_reviewed: { icon: CheckCircle2, tone: 'text-success' },
  review_mode_changed: { icon: CircleDot, tone: 'text-ink-secondary' },
  review_policy_changed: { icon: CircleDot, tone: 'text-ink-secondary' },
  commerce_published: { icon: Upload, tone: 'text-warning' },
  payout_created: { icon: Wallet, tone: 'text-ink-secondary' },
  payout_paid: { icon: Wallet, tone: 'text-success' },
  payout_voided: { icon: Wallet, tone: 'text-ink-muted' },
  payout_updated: { icon: Wallet, tone: 'text-ink-secondary' },
};

interface HistoryEntry {
  key: string;
  action: string;
  actor: string | null;
  created_at: string;
  note: string | null;
  /** Field changes saved together (one owner or admin save). */
  changes: AdminStorefrontEvent[];
}

/** Field edits saved in one request share an actor and a timestamp: one
 *  history entry lists them together. */
function historyEntries(events: AdminStorefrontEvent[]): HistoryEntry[] {
  const sorted = [...events].sort(
    (a, b) => Date.parse(b.created_at ?? '') - Date.parse(a.created_at ?? ''),
  );
  const out: HistoryEntry[] = [];
  sorted.forEach((event, i) => {
    const last = out[out.length - 1];
    const isEdit = event.kind === 'change' && event.action === 'edited';
    if (
      isEdit &&
      last?.action === 'edited' &&
      last.actor === event.actor &&
      Math.abs(Date.parse(last.created_at) - Date.parse(event.created_at)) < 2000
    ) {
      last.changes.push(event);
      return;
    }
    out.push({
      key: eventKey(event, i),
      action: event.action,
      actor: event.actor,
      created_at: event.created_at,
      note: event.kind === 'change' ? null : event.note,
      changes: event.kind === 'change' ? [event] : [],
    });
  });
  return out;
}

export function EventsPanel({
  events,
  productNames = {},
}: {
  events: AdminStorefrontEvent[];
  productNames?: Record<string, string>;
}) {
  const entries = historyEntries(events);
  return (
    <Panel title="History" aside={<span className="type-label-xs text-ink-muted">{entries.length}</span>}>
      {entries.length === 0 ? (
        <p className="type-body-sm text-ink-muted">Nothing yet.</p>
      ) : (
        <ol className="flex flex-col">
          {entries.map((entry, i) => {
            const meta = EVENT_META[entry.action] ?? { icon: CircleDot, tone: 'text-ink-muted' };
            const Icon = meta.icon;
            const label =
              entry.action === 'edited'
                ? entry.changes.length > 1
                  ? `Edited ${entry.changes.length} fields`
                  : 'Edited'
                : (EVENT_LABEL[entry.action] ?? entry.action.replace(/_/g, ' '));
            return (
              <li key={entry.key} className="relative flex gap-12 pb-16 last:pb-0">
                {i < entries.length - 1 && (
                  <span aria-hidden className="absolute left-[9px] top-22 bottom-0 w-px bg-line-subtle" />
                )}
                <Icon size={19} className={cn('shrink-0 bg-page', meta.tone)} aria-hidden />
                <div className="flex flex-col gap-4 min-w-0">
                  <span className="type-body-sm text-ink">{label}</span>
                  <span className="flex flex-wrap items-baseline gap-x-6 min-w-0">
                    <span className="type-label-xs text-ink-muted">{dateTime(entry.created_at)}</span>
                    {entry.actor && (
                      <span className="type-body-sm text-ink-muted truncate max-w-full" title={entry.actor}>
                        {entry.actor}
                      </span>
                    )}
                  </span>
                  {entry.changes.length > 0 &&
                    (entry.changes.every((c) => c.action.startsWith('payout_')) ? (
                      <ul className="flex flex-col gap-4 type-body-sm leading-130 mt-2">
                        {entry.changes.map((change, j) => (
                          <li key={eventKey(change, j)} className="text-ink-secondary">
                            {eventSentence(change, productNames)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ChangeList
                        className="mt-2"
                        productNames={productNames}
                        changes={entry.changes.map((c) => ({
                          field: c.field ?? '',
                          old: c.old_value ?? null,
                          new: c.new_value ?? null,
                        }))}
                      />
                    ))}
                  {entry.note && (
                    <p className="type-body-sm text-ink-secondary whitespace-pre-wrap leading-130 border-l-2 border-line-subtle pl-8 mt-2">
                      {entry.action === 'changes_published' ||
                      entry.action === 'changes_submitted' ||
                      entry.action === 'commerce_published'
                        ? readableNote(entry.note, productNames)
                        : entry.action === 'review_policy_changed'
                          ? readablePolicyNote(entry.note)
                          : entry.note}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}

export function ProductsWarning({ store }: { store: AdminStorefrontDetail }) {
  const below = store.products.flatMap((p) =>
    p.plans.filter((plan) => plan.active && plan.floor_cents != null && plan.price_cents < plan.floor_cents),
  );
  if (below.length === 0) return null;
  return (
    <Notice tone="danger">
      {below.length} active plan{below.length === 1 ? ' is' : 's are'} priced below floor. The
      store cannot go live until the owner fixes {below.length === 1 ? 'it' : 'them'}.
    </Notice>
  );
}
